import { useTranslation } from 'react-i18next'
import { updateCharacter } from '../../../api/character'

import { Button } from '@/components/ui/button'

import Timestamp from 'app/frontend/base/components/Timestamp'
import TeamCard from 'app/frontend/base/components/TeamCard'
import { useRefreshCharacterDetails } from 'app/frontend/base/hooks/useRefreshCharacterDetails'
import { usePageInfo } from 'app/frontend/base/config/queries'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface Props {
  character?: wowaudit.Character
  refetch: () => void
}

const TrackingSummary = ({ character, refetch }: Props) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  const { refreshing, refreshData } = useRefreshCharacterDetails(character, {
    retriever: updateCharacter,
    id: character?.id,
    successKey: 'dashboard.character.character-update-success',
    callback: refetch,
  })

  return (
    <div className='min-h-40 space-y-1'>
      <p>
        {character?.teamReferences?.length
          ? t('dashboard.character.tracked-in-teams', {
              count: character?.teamReferences?.length,
            })
          : t('dashboard.character.not-tracked-in-teams')}
      </p>
      {(character?.teamReferences || []).map((team) => (
        <TeamCard key={team.id} team={team} />
      ))}

      <div className='mt-3 mb-1 pb-1 block border-b font-bold'>
        {t('shared.last-updated')}
      </div>

      {character?.details && !character?.refreshedAt ? (
        <p className='italic opacity-70'>
          {t('dashboard.character.never-refreshed')}
        </p>
      ) : null}

      <div className='flex justify-between items-center'>
        <Timestamp time={character?.refreshedAt} colorNever={true} />

        {user?.canRefreshManually ? (
          <Button
            variant='outline'
            size='sm'
            loading={refreshing}
            spinner='blizzard'
            disabled={!character?.id}
            onClick={() => refreshData()}
          >
            {t('shared.update-now')}
          </Button>
        ) : (
          <Tooltip>
            <TooltipTrigger className='cursor-not-allowed'>
              <Button size='sm' variant='outline' disabled>
                {t('shared.update-now')}
              </Button>
            </TooltipTrigger>
            <TooltipContent className='flex flex-col gap-1'>
              <p>
                Refreshing on demand is only available for users with a
                subscription tier of at least{' '}
                <span className='font-semibold text-quality-epic'>Epic</span>.
              </p>

              <p>
                If this character is in a team, it will update on a schedule
                automatically.
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  )
}

export default TrackingSummary
